import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { useNavigate } from 'react-router';
import styles from './_application-queue-tabs.module.scss';
import {Constants, millisecondsToRefreshQueue} from "../../../utils/constants";
import {addThousandsSeparator} from '@amsanalytics/react-shared-library';
import {
  clearAllApplicationQueuesFiltersSuccess,
  CREATE_APPLICATION_SUCCESS,
  loadApplicationQueueCounts
} from "../../../store/application/types";
import { AppState } from '../../../store/rootReducer';
import {useConfiguration} from "../../../Data/ConfigurationData";
import {loadApps} from "../../../store/application/actions";
import {IGetApplicationQueueCounts} from "../../../Interfaces/Common/IApplicationQueue";
import ApplicationQueue from "./ApplicationQueue/ApplicationQueue";

const ApplicationQueueTabs: React.FC = () => {
  const {data: configuration} = useConfiguration();
  const applicationQueues = useSelector((state: AppState) => state.applications.lookups.applicationQueues);
  const applicationQueueDetails = useSelector((state: AppState) => state.applications.applicationQueueDetails);
  // Page history. Required for us to navigate user to "new application" page.
  const navigate = useNavigate();

  const newlyCreatedApplicationId: string | undefined = useSelector((state: AppState) => state.applications.newApplicationId);
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const useTabsOnLeftSide = applicationQueues.length > 5;
  
  useEffect(() => {
    if (newlyCreatedApplicationId !== null && newlyCreatedApplicationId !== undefined) {
      dispatch({
        type: CREATE_APPLICATION_SUCCESS,
        payload: { applicationId: undefined },
      });
      navigate(`/order/${newlyCreatedApplicationId}/true`);
    }
  }, [newlyCreatedApplicationId]);
  
  const defaultStatus = configuration?.featureFlags?.[FeatureFlagIds.DefaultToUnderwriting] === true
    ? Constants.applicationStatuses.underwriting.toLowerCase()
    : '';

  const fetchAllCounts = (fetchWithClearedFilters: boolean) => {
    dispatch(loadApps());
    const queueFilterDetails: IGetApplicationQueueCounts[] = applicationQueueDetails.map(aq => {
      return {
        queueId: aq.id,
        filterOptions: fetchWithClearedFilters // This is used mainly from clearAllQueueFilters() where the filters are cleared, but this is called before it takes effect
          ? {...aq.filters, filter: undefined}
          : aq.filters
      }
    });

    dispatch(loadApplicationQueueCounts(queueFilterDetails))
  };

  useEffect(() => {
    if (applicationQueues.length === 0) {
      return;
    }

    fetchAllCounts(false); // called at the beginning
    
    const timer = setInterval(() => {
      fetchAllCounts(false);
    }, millisecondsToRefreshQueue);

    return () => clearInterval(timer);
  }, [applicationQueues.length]);
  
  const clearAllQueueFilters = () => {
    dispatch(clearAllApplicationQueuesFiltersSuccess());
    fetchAllCounts(true);
  }

  // Runs once when the component mounts to clear all queue filters if the feature flag is enabled.
  useEffect(() => {
    if (configuration?.featureFlags[FeatureFlagIds.ClearAllApplicationQueueFiltersWhenOpeningQueueComponent])  {
      clearAllQueueFilters();
    }
  }, []);

  return (
    <div className={styles.applicationQueueTabsContainer}>
       <TabStrip
         style={{ height: '100%' }} 
         tabPosition={useTabsOnLeftSide ? "left" : "top"}
         animation={false} 
         selected={selectedTab} 
         scrollable={true}
         keepTabsMounted={true}
         onSelect={(e: TabStripSelectEventArguments) => setSelectedTab(e.selected)}
        >
          {applicationQueues.map((aq, key) => {
            const detailForQueue = applicationQueueDetails.find(aqd => aqd.id === aq.id);
            return <TabStripTab 
              contentClassName={styles.tabContent}
              title={<div className={styles.tabTitle}><span>{aq.name}</span> <span>({addThousandsSeparator(detailForQueue!.count)})</span></div>}
              key={key}
            >
              {configuration && applicationQueueDetails.length > 0 && selectedTab === key &&
                <ApplicationQueue
                  applicationQueue={aq}
                  applicationQueueLoadingDetail={detailForQueue!}
                  defaultStatus={defaultStatus}
                  configuration={configuration}
                />
              }
            </TabStripTab>
          })}
       </TabStrip>      
    </div>
  );
};

export default ApplicationQueueTabs;